import { Setter, atom, useAtom, useAtomValue, useSetAtom } from 'jotai'

import { TUserInfo } from '@/api'
import { getCookieAuthInfo, isMobile, removeCookieAuthInfo } from '@/utils'

const initUserInfo = {
  avatar_url: '',
  balance: 0,
  birthday: '',
  description: null,
  email: '',
  followers: 0,
  following: 0,
  gender: 0,
  id: '',
  income: 0,
  mobile: null,
  nickname: null,
  quantity: 0,
  referral_code: '',
  region: null,
  author_id: '',
  register_type: '',
  created_at: '',
  bonus: '',
  score: 0,
  vip_card: {
    available: false,
    start_at: null,
    end_at: null,
  },
}

export const getLoginStateFromCookie = () => {
  const { token, refreshToken } = getCookieAuthInfo('token', 'refreshToken')
  return !!(token || refreshToken)
}

const isTokenExpired = () => {
  const { tokenExpire } = getCookieAuthInfo('tokenExpire')
  if (!tokenExpire) {
    return false
  }

  return Date.now() >= Number(tokenExpire) * 1000
}

// 私有 revision：唯一可变状态。任何想强制让 loginStateAtom 重新求值的地方都 set 它一下。
const loginStateRevisionAtom = atom(0)
loginStateRevisionAtom.debugLabel = 'loginStateRevisionAtom'

// 公开的派生 atom：依赖 revision + 实时读 cookie + 检查过期。
// 这样 bfcache 恢复时调用 store.set(syncLoginStateAtom) 走 revision bump 路径，
// 就能让所有 useAtomValue(loginStateAtom) 的订阅者拿到新值并重渲染。
export const loginStateAtom = atom<boolean>((get) => {
  get(loginStateRevisionAtom)
  return getLoginStateFromCookie() && !isTokenExpired()
})
loginStateAtom.debugLabel = 'loginStateAtom'

export const userAtom = atom<TUserInfo>(initUserInfo)
userAtom.debugLabel = 'userAtom'

// 所有"登出/失效"路径共用的副作用：清空 token 相关 cookie 并把 userAtom 还原到初始值。
const logoutSideEffects = (set: Setter) => {
  removeCookieAuthInfo('token')
  removeCookieAuthInfo('refreshToken')
  removeCookieAuthInfo('tokenExpire')
  set(userAtom, initUserInfo)
}

// 同步 cookie 登录态到 atom：每次调用都 bump revision，强制所有订阅者重新计算。
// 当 cookie 失效（派生值回退到 false）时还原 userAtom 为初始值，避免内存里残留旧用户信息。
// 必须在 pageshow 等场景下被显式调用，否则 bfcache 恢复后内存中的派生值与真实 cookie 不一致。
export const syncLoginStateAtom = atom(null, (_get, set) => {
  const next = getLoginStateFromCookie() && !isTokenExpired()
  if (!next) {
    logoutSideEffects(set)
  }
  set(loginStateRevisionAtom, (v: number) => v + 1)
})

// 用于 axios 等命令式场景：传 true/false 表示期望的登录态，false 时清 cookie 并还原 userAtom。
export const setLoginStateAtom = atom(null, (_get, set, next: boolean) => {
  if (!next) {
    logoutSideEffects(set)
  }
  set(loginStateRevisionAtom, (v: number) => v + 1)
})

export const useLoginStateAtom = () => {
  const isLoginState = useAtomValue(loginStateAtom)
  const setLoginState = useSetAtom(setLoginStateAtom)

  return {
    isLoginState,
    setIsLoginState: (next: boolean) => setLoginState(next),
  }
}

export const useUserAtom = () => {
  const [user, setUser] = useAtom(userAtom)

  return {
    user,
    setUser,
  }
}

export const mobileStatusAtom = atom(isMobile())

export const useMobileStatusAtom = () => {
  const [mobileStatus, setMobileStatus] = useAtom(mobileStatusAtom)

  return {
    mobileStatus,
    setMobileStatus,
  }
}
